"use client";

import Image from "next/image";
import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CheckBox, YoutubePlay } from "@/components/icons/assets";

const YOUTUBE_VIDEO_ID = "he3QGj-tjfE";

const features = [
  "Universal fit for most vehicles",
  "Installs in minutes",
  "Safe movement without wheels",
  "Built for heavy-use workshops",
] as const;

export default function WhyChoose() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <section
        id="why-choose"
        className="bg-background-50 px-4 sm:px-6 lg:px-10 py-[60px] xl:py-[100px]"
      >
        <div className="w-full max-w-[1440px] mx-auto flex flex-col gap-7.5 md:gap-[60px]">

          {/* ── Top Block ── */}
          <div className="flex flex-col gap-4 md:gap-[30px] w-full xl:w-[1360px] h-auto xl:h-[220px]">
            <h2 className="font-oswald font-medium text-[32px] sm:text-[42px] lg:text-[60px] leading-[1.2] lg:leading-[80px] tracking-[0.2px] uppercase text-neutral-500 w-full lg:w-[1360px] h-auto lg:h-[80px]">
              Why Choose Guniwheel?
            </h2>
            <p className="font-roboto font-normal text-[14px] sm:text-[16px] xl:text-[18px] leading-[25px] tracking-[0%] text-neutral-300 w-full xl:w-[1360px] h-auto xl:h-[50px]">
              Guniwheel is engineered to replace traditional dollies, jack stands, and temporary wheel solutions. It delivers unmatched safety, speed, and convenience for busy workshops and restoration experts.
            </p>

            {/* Features row */}
            <ul className="flex flex-col sm:flex-row flex-wrap gap-4 xl:gap-0 xl:justify-between w-full xl:w-[1320px] xl:h-[30px]">
              {features.map((feature) => (
                <li key={feature} className="flex items-center gap-2">
                  <CheckBox className="flex-shrink-0" aria-hidden="true" />
                  <span className="font-oswald font-medium text-[16px] xl:text-[20px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500">
                    {feature}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Video Thumbnail ── */}
          <div className="relative w-full h-[250px] sm:h-[400px] xl:h-[690px] rounded-[12px] overflow-hidden p-[10px] bg-background-50">
            <Image
              src="/image/home/why-choose-thumbnail.webp"
              alt="GuniWheel product presentation — two workshop professionals demonstrating the universal wheel system"
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1280px) 100vw, 1360px"
              className="object-cover rounded-[12px]"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <button
                type="button"
                onClick={() => setIsOpen(true)}
                aria-label="Play Why Choose Guniwheel video"
                className="cursor-pointer hover:scale-110 transition-transform scale-75 sm:scale-90 xl:scale-100"
              >
                <YoutubePlay aria-hidden="true" />
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* ── YouTube Modal ── */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-[90vw] xl:max-w-[1000px] p-0 border-none bg-transparent shadow-none [&>button]:hidden">
          <DialogTitle className="sr-only">Why Choose Guniwheel Video</DialogTitle>
          {isOpen && (
            <iframe
              src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1`}
              title="Why Choose Guniwheel — product demonstration video"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              frameBorder="0"
              className="w-full aspect-video rounded-[12px]"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}